// The workbench pane — the frame around an action, plus all zen chrome
// (chips, filmstrip, swipe target). The action itself is rendered by the
// shared <ActionPage/>, exactly as the Book renders it; what lives here is
// only what the Book has no use for: zen navigation, the pane's own close,
// and the re-entry card.
import React from 'react';
import { X } from 'lucide-react';
import ActionPage from './ActionPage';
import ClipRack from './ClipRack';

export default function DetailPane({
  selectedAction,
  selectedActionId,
  setSelectedActionId,
  zenMode,
  setZenMode,
  filmstrip,
  setFilmstrip,
  displayedActions,
  navigateZen,
  zenNavDirRef,
  zenPaneRef,
  paneOverlay,
  actionEditorRef,
  updateAction,
  deleteAction,
  setActionStatus,
  addChecklistItem,
  toggleActionPho,
  toggleActionComplete,
  projects,
  goals,
  getProjectColorById,
  formatDateTime,
  dismissedColdOpenFor,
  setDismissedColdOpenFor,
  timeAgo,
  onOpenGoal,
  onOpenProject,
  zenClickEnterRef,
  calm,       // the room: write in place — no zen hijack, no crumb
  fixedOpen,  // the room list: the note is always open, no close button
  dock,         // 'right' (inspector) or 'bottom' (the DAW clip view)
  onDockChange, // present where the dock is the user's choice (the wall)
  completeWithReceipt, // the rack closes a task with its receipt, no modal
  narrow,       // the room board: the pane keeps a reading width, the board flexes
}) {
  if (!selectedAction) return null;
  const bottomDock = dock === 'bottom' && !zenMode && !paneOverlay;

  return (
            <div
              ref={zenPaneRef}
              key={`pane-${selectedActionId}`}
              className={`overflow-y-auto themed-background ${(bottomDock || narrow) ? 'min-w-0' : 'flex-1 min-w-0'} ${zenMode ? 'zen-enter' : ''} ${zenMode && zenNavDirRef.current !== 0 ? (zenNavDirRef.current > 0 ? 'zen-arrive-next' : 'zen-arrive-prev') : ''}`}
              style={zenMode
                ? { minHeight: 'calc(100vh - 56px)', maxWidth: '860px', margin: '0 auto', overscrollBehaviorX: 'none', touchAction: 'pan-y' }
                : paneOverlay
                  ? { position: 'fixed', top: '44px', right: 0, bottom: 0, width: 'min(620px, 100vw)', zIndex: 40, boxShadow: '-12px 0 40px rgba(0,0,0,0.18)', borderLeft: '1px solid var(--color-border)' }
                  // the clip view: full width along the bottom, like a DAW —
                  // the floor above keeps every strip visible
                  : bottomDock
                  ? { position: 'fixed', left: 0, right: 0, bottom: 0, height: 'auto', zIndex: 45, borderTop: '1px solid var(--color-border)', boxShadow: '0 -24px 60px rgba(0,0,0,0.25)', background: 'var(--color-background)', overflow: 'hidden' }
                  : narrow
                  ? { width: 'min(720px, 48vw)', flexShrink: 0 }
                  // calm (the room): the row fixes the height, so the pane
                  // scrolls by itself — a minHeight would grow the page instead
                  : calm ? undefined
                  : { minHeight: 'calc(100vh - 56px)' }}
            >
              {/* Zen chips: prev / position / next / exit */}
              {zenMode && (() => {
                const idx = displayedActions.findIndex(g => g.id === selectedActionId);
                const chip = "flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium themed-text-muted hover:themed-text border themed-border themed-background shadow-sm transition-colors";
                return (
                  <div data-zen-chrome className="fixed top-14 right-5 z-50 flex items-center gap-1.5">
                    {displayedActions.length > 1 && (
                      <>
                        <button onClick={() => navigateZen(-1)} className={chip} title="Previous action (↑ or ⌘⇧[)">‹</button>
                        <button
                          onClick={() => setFilmstrip(f => !f)}
                          className="text-[10px] themed-text-muted hover:themed-text tabular-nums select-none bg-transparent border-0 cursor-pointer"
                          title="All notes in this view (click) — Esc closes it"
                        >
                          {idx === -1 ? '·' : `${idx + 1}/${displayedActions.length}`}
                        </button>
                        <button onClick={() => navigateZen(1)} className={chip} title="Next action (↓ or ⌘⇧])">›</button>
                      </>
                    )}
                    <button onClick={() => setZenMode(false)} className={chip} title="Exit zen (H or Esc)">
                      <X size={11} /> esc
                    </button>
                  </div>
                );
              })()}

              {/* The filmstrip opens deliberately — from the counter chip or
                  the keyboard. It used to spring from an invisible 30px strip
                  across the top of the screen, which sat over the nav and
                  fired on the way to it. Hover-opened panels ambush the
                  cursor; the app bans them everywhere else too. */}

              {/* Filmstrip — every note in the current lens, click to open */}
              {zenMode && filmstrip && (
                <div
                  data-zen-chrome className="fixed top-0 left-0 right-0 z-50 themed-background border-b themed-border shadow-lg zen-filmstrip"
                  onMouseLeave={() => setFilmstrip(false)}
                >
                  <div className="strip-scroll flex gap-2.5 px-4 py-3 overflow-x-auto">
                    {displayedActions.map((g, i) => {
                      const current = g.id === selectedActionId;
                      // Project identity: only when the lens spans multiple projects —
                      // a 2px color edge (zero card-size cost) + full name on hover
                      const multiProject = new Set(displayedActions.map(x => x.linkId)).size > 1;
                      const proj = g.linkType === 'project' ? projects.find(p => p.id === g.linkId) : null;
                      const snippet = (g.notes || '')
                        .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
                        .replace(/[#>*`_[\]()-]/g, ' ')
                        .replace(/\s+/g, ' ')
                        .trim()
                        .slice(0, 90);
                      const curIdx = displayedActions.findIndex(x => x.id === selectedActionId);
                      return (
                        <button
                          key={g.id}
                          onClick={() => {
                            zenNavDirRef.current = i >= curIdx ? 1 : -1;
                            setSelectedActionId(g.id);
                            setFilmstrip(false);
                          }}
                          className="flex-shrink-0 w-44 text-left rounded-lg border p-2.5 transition-all hover:shadow-md themed-background"
                          title={proj ? `${proj.name} › ${g.text}` : g.text}
                          style={{
                            ...(current
                              ? { borderColor: 'var(--color-primary)', boxShadow: '0 0 0 1px var(--color-primary)', background: 'var(--color-surface)' }
                              : { borderColor: 'var(--color-border)' }),
                            ...(multiProject && proj ? { borderTop: `2.5px solid ${getProjectColorById(proj.id)}` } : {}),
                          }}
                        >
                          <div className="flex items-start gap-1">
                            {g.pho && <span className="text-[9px] flex-shrink-0" style={{ color: 'var(--color-pho)' }}>✦</span>}
                            <span className="text-[12px] font-semibold themed-text leading-snug" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                              {g.text}
                            </span>
                          </div>
                          {snippet && (
                            <p className="text-[10px] themed-text-muted mt-1 leading-snug" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                              {snippet}
                            </p>
                          )}
                          {g.status === 'doing' && (
                            <span className="inline-block mt-1.5 text-[8px] font-medium px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-500 border border-blue-200">In Progress</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {/* The pane closes from here — the page inside knows nothing about
                  panes. In zen the chips own the top-right corner, so this
                  stands down; esc brings the pane (and this ✕) back. */}
              {bottomDock && (
                <ClipRack
                  action={selectedAction}
                  updateAction={updateAction}
                  setActionStatus={setActionStatus}
                  toggleActionComplete={toggleActionComplete}
                  completeWithReceipt={completeWithReceipt}
                  toggleActionPho={toggleActionPho}
                  addChecklistItem={addChecklistItem}
                  setSelectedActionId={setSelectedActionId}
                  dock={dock}
                  onDockChange={onDockChange}
                />
              )}
              {!bottomDock && (<>
              <div className={`px-10 flex justify-end items-center gap-2 ${zenMode ? 'pt-8' : 'pt-6'}`}>
                {/* dock choice, like a DAW: ⬓ clip view below, ◨ inspector right */}
                {!zenMode && onDockChange && (<>
                  <button onClick={() => onDockChange('bottom')}
                    className="w-6 h-6 rounded border text-[12px] leading-none"
                    style={dock === 'bottom'
                      ? { color: 'var(--color-text)', borderColor: 'var(--color-primary)' }
                      : { color: 'var(--color-textMuted)', borderColor: 'var(--color-border)' }}
                    title="Dock the editor at the bottom, like a clip view">⬓</button>
                  <button onClick={() => onDockChange('right')}
                    className="w-6 h-6 rounded border text-[12px] leading-none"
                    style={dock === 'right'
                      ? { color: 'var(--color-text)', borderColor: 'var(--color-primary)' }
                      : { color: 'var(--color-textMuted)', borderColor: 'var(--color-border)' }}
                    title="Dock the editor on the right, like an inspector">◨</button>
                </>)}
                {/* fixedOpen: the room-list note is furniture, not a popup —
                    there is nothing to close */}
                {!zenMode && !fixedOpen && (
                  <button
                    onClick={() => setSelectedActionId(null)}
                    className="p-1.5 rounded themed-text-muted hover:themed-text transition-colors"
                    title="Close (Esc)"
                  >
                    <X size={16}/>
                  </button>
                )}
              </div>

              {/* The cold re-entry banner lived here until Oct 7 2026 —
                  Paul: "I don't like the whole re-entering bit. It's noise."
                  The note itself is the re-entry. */}

              {/* The action itself — the same page the Book renders.
                  In the clip view the page gets a reading measure, centered. */}
              <div className="px-10 pt-2 pb-8" style={bottomDock ? { maxWidth: 880, margin: '0 auto' } : undefined}>
                <ActionPage
                  action={selectedAction}
                  projects={projects}
                  goals={goals}
                  updateAction={updateAction}
                  setActionStatus={setActionStatus}
                  toggleActionPho={toggleActionPho}
                  toggleActionComplete={toggleActionComplete}
                  addChecklistItem={addChecklistItem}
                  onDelete={(id) => { if (deleteAction(id)) setSelectedActionId(null); }}
                  onOpenGoal={onOpenGoal}
                  onOpenProject={onOpenProject}
                  timeAgo={timeAgo}
                  editorRef={actionEditorRef}
                  onEditorNavigate={zenMode ? (dir) => navigateZen(dir, true) : undefined}
                  // In the room, clicking into the note just starts writing,
                  // right where it is — Bear-like. Elsewhere the old intent
                  // stands: clicking in goes zen.
                  onEditorFocus={calm ? undefined : () => {
                    if (!zenMode) { zenClickEnterRef.current = true; setZenMode(true); }
                  }}
                  hideCrumb={calm}
                />
              </div>


              {selectedAction.createdDate && (
                <div className="px-10 py-3 border-t themed-border-light">
                  <p className="text-xs themed-text-muted opacity-40">Created {formatDateTime(selectedAction.createdDate)}</p>
                </div>
              )}
              </>)}
            </div>
  );
}
